// Test Learning History - Demo Page
// Test useStudyTracking và useQuizTracking hooks

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStudyTracking } from '../hooks/useStudyTracking';
import { useQuizTracking } from '../hooks/useQuizTracking';
import Navigation from '../components/common/Navigation';
import AchievementNotification from '../components/achievement/AchievementNotification';
import { Achievement, QuizAnswer } from '../types/learningHistory';

export default function TestLearningHistory() {
  const { user } = useAuth();
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);
  
  // Test study tracking
  const {
    trackingData: studyData,
    updateProgress,
    completeLesson,
    addBookmark
  } = useStudyTracking({
    lessonId: 'TEST_L12_C4_B01',
    lessonTitle: 'Demo - Dao động điều hòa',
    autoStart: true
  });

  // Test quiz tracking
  const {
    trackingData: quizData,
    saveQuizResult
  } = useQuizTracking({
    quizId: 'test-quiz-123',
    lessonId: 'TEST_L12_C4_B01',
    quizTitle: 'Demo Quiz - Dao động',
    quizType: 'lesson',
    timeLimitSeconds: 600
  });

  const handleUpdateProgress = async () => {
    const newProgress = Math.min(100, (studyData?.progress || 0) + 25);
    await updateProgress(newProgress);
    console.log(`✅ Updated progress to ${newProgress}%`);
  };

  const handleCompleteLesson = async () => {
    await completeLesson();
    console.log('✅ Lesson completed!');
  };

  const handleAddBookmark = async () => {
    await addBookmark('section-demo-1');
    console.log('✅ Bookmark added!');
  };

  const handleSaveQuiz = async () => {
    const demoAnswers: QuizAnswer[] = [
      {
        questionId: 'q1',
        questionText: 'Demo question',
        questionType: 'TN',
        userAnswer: 'A',
        correctAnswer: 'A',
        isCorrect: true,
        timeSpent: 30
      }
    ];

    const result = await saveQuizResult(
      85, // score
      10, // totalQuestions
      8, // correctAnswers
      2, // wrongAnswers
      0, // skippedAnswers
      demoAnswers
    );

    if (result) {
      console.log('✅ Quiz result saved:', result.result);
      if (result.achievements.length > 0) {
        setNewAchievements(result.achievements);
        console.log('🏆 Achievements unlocked:', result.achievements);
      }
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Vui lòng đăng nhập để test</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🧪 Test Learning History Hooks
          </h1>
          <p className="text-gray-600 mb-6">
            Test useStudyTracking và useQuizTracking trong thực tế
          </p>

          {/* Study Tracking Section */}
          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-blue-800 mb-4">
              📚 Study Tracking
            </h2>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Session ID:</span>
                <span className="font-mono text-sm bg-white px-3 py-1 rounded">
                  {studyData.sessionId || 'Not started'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Progress:</span>
                <div className="flex items-center gap-3">
                  <div className="w-48 bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all"
                      style={{ width: `${studyData.progress}%` }}
                    />
                  </div>
                  <span className="font-bold text-blue-600">
                    {studyData.progress}%
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Time Spent:</span>
                <span className="font-semibold">
                  {studyData.timeSpent}s ({Math.floor(studyData.timeSpent / 60)} phút)
                </span>
              </div>
              
              <div className="bg-blue-100 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-xs text-blue-700">
                  ⏱️ <strong>Note:</strong> Thời gian học được tự động cập nhật mỗi 5 giây. 
                  Đợi vài giây để thấy "Time Spent" tăng lên!
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Is Tracking:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  studyData.isTracking ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {studyData.isTracking ? '✅ Active' : '❌ Inactive'}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleUpdateProgress}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                +25% Progress
              </button>
              
              <button
                onClick={handleAddBookmark}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                Add Bookmark
              </button>
              
              <button
                onClick={handleCompleteLesson}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Complete Lesson
              </button>
            </div>
          </div>

          {/* Quiz Tracking Section */}
          <div className="bg-green-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-green-800 mb-4">
              📝 Quiz Tracking
            </h2>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Is Saving:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  quizData.isSaving ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                }`}>
                  {quizData.isSaving ? '⏳ Saving...' : '✅ Ready'}
                </span>
              </div>
              
              {quizData.lastResult && (
                <div className="bg-white rounded p-3">
                  <div className="text-sm font-semibold text-gray-800 mb-2">
                    Last Result:
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Score: <span className="font-bold text-green-600">{quizData.lastResult.score}</span></div>
                    <div>Attempt: #{quizData.lastResult.attemptNumber}</div>
                    <div>Status: {quizData.lastResult.passed ? '✅ Đạt' : '❌ Chưa đạt'}</div>
                  </div>
                </div>
              )}
              
              {quizData.newAchievements.length > 0 && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded p-3">
                  <div className="text-sm font-semibold text-yellow-800 mb-2">
                    🏆 New Achievements: {quizData.newAchievements.length}
                  </div>
                  {quizData.newAchievements.map(a => (
                    <div key={a.id} className="text-sm text-yellow-700">
                      {a.icon} {a.title}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleSaveQuiz}
              disabled={quizData.isSaving}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {quizData.isSaving ? 'Đang lưu...' : 'Save Demo Quiz Result (Score: 85)'}
            </button>
          </div>
        </div>

        {/* Console Logs Help */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-yellow-800 mb-3">
            📖 Hướng dẫn xem logs
          </h3>
          <div className="text-yellow-700 text-sm space-y-2">
            <p>1. Mở Console: Nhấn <kbd className="px-2 py-1 bg-white rounded border">F12</kbd></p>
            <p>2. Click tab "Console"</p>
            <p>3. Click các buttons phía trên</p>
            <p>4. Xem logs xuất hiện với prefix:</p>
            <div className="bg-white rounded p-2 font-mono text-xs mt-2">
              📖 [useStudyTracking] ...<br/>
              📝 [useQuizTracking] ...<br/>
              🏆 [AchievementService] ...
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Notification */}
      {newAchievements.length > 0 && (
        <AchievementNotification
          achievements={newAchievements}
          onClose={() => setNewAchievements([])}
        />
      )}
    </div>
  );
}

